<template>
  <div class="campaign-product-list critical-component-clear">
    <div class="container">
      <badge-filter
        v-if="liquid.showFilter"
        show-filter
        :filter-options="filterOptions"
        :filters="filters"
        :sorting="liquid.filterSorting"
        :loading="initialLoad"
        prefix="-"
        suffix="%"
        :items-count="getFilteredProducts.length"
        total-items-label=""
        :no-result-message="$string('collection.no_products_filter_html')"
        @reset-filters="resetFilters"
        @toggle-filter="
          (payload) => toggleBadgeFilter(payload.key, payload.option)
        "
      />
      <product-grid
        v-if="getFilteredProducts.length || state === 'loading'"
        :campaign-products="getFilteredProducts"
        :campaign-loading="initialLoad"
        :discount-preview="discountPreview"
        :next-campaign-page="hasNextPage"
        :partial="partial"
        :products-per-page="productCount"
        template="campaign"
        :hide-soldout="liquid.hideSoldout"
        :is-filter-shown="liquid.showFilter"
        :handle-btn-click="getProductsByList"
        :parent-state="state"
        :infinite-scroll="liquid.infiniteScroll"
        :disturber="disturber"
      />

      <div v-else class="campaign-product-list__empty-state text-center">
        <div
          v-if="liquid.title"
          class="campaign-product-list__title text-mobile-headlines-h4 text-desktop-headlines-h4"
          v-text="liquid.title"
        />

        <div
          v-if="liquid.text"
          class="campaign-product-list__text text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
          v-text="liquid.text"
        />

        <btn
          v-if="liquid.cta.text && liquid.cta.link"
          class="campaign-product-list__cta"
          :label="liquid.cta.text"
          modifiers="secondary block center"
          :url="liquid.cta.link"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Campaign product list (campaign-product-list)
 * -----------------------------------------------------------------------------
 * Campaign product list.
 *
 * @param {Array} discounts - Campaign metafield discounts.
 * @param {Boolean} discountPreview - Show preview of promoted campaign in
 * product card badges and prices.
 * @param {Boolean} initialPartial - Show only 4 products initially.
 * @param {String} liquid.cta -  Empty state - CTA text and URL.
 * @param {String} liquid.text -  Empty state - Section Text.
 * @param {String} liquid.title -  Empty state - Section Title.
 */

import Btn from '~global/btn/btn';
import ProductGrid from '~async/product-grid/product-grid';
import BadgeFilter from '~async/badge-filter/badge-filter';

import excludedNodesByIdQuery from '~graphql/excluded-nodes-by-id.gql';
import nodesByIdProductCardQuery from '~graphql/nodes-by-id-product-card.gql';
import fetchMetaobjectCard from '~graphql/metaobject-card.gql';

import {
  formatCampaign,
  formatCampaignProducts,
  formatExcludedCollection,
} from '~/format/format-campaign';
import { formatProduct } from '~/format/format-product';
import {
  decodeApiId,
  removeEdges,
  transformEdges,
  transformId,
} from '~/helpers/graphql';

import {
  buildFilterOptions,
  filteredProducts,
  getPageNumber,
  getProductDiscount,
  parseURLParams,
  updateURL,
} from '~/helpers/filter';

export default {
  name: 'CampaignProductList',

  components: { ProductGrid, BadgeFilter, Btn },

  provide() {
    return { variantsShowCount: this.liquid.variantsShowCount };
  },

  props: {
    disturber: {
      type: Array,
      default: () => [],
    },
    discounts: { type: Array, default: () => [] },
    discountPreview: { type: Boolean, default: true },
    initialPartial: { type: Boolean, default: false },
    liquid: {
      type: Object,
      default: () => ({
        cta: { text: '', url: '' },
        productSorting: [],
        text: '',
        title: '',
        showFilter: false,
        filterSorting: 'Ascending',
        hideSoldout: false,
        productList: [],
        useCacheList: false,
        infiniteScroll: true,
        combineVariants: true,
        variantsShowCount: true,
        promoFilterText: '',
        promoFilterHandle: '',
      }),
    },
    productCount: { type: Number, default: 0 },
  },

  data() {
    return {
      allProducts: [],
      filterOptions: {},
      filters: {},
      removeFilters: {},
      initialLoad: false,
      partial: this.initialPartial,
      products: [],
      preparedCacheList: [],
      state: 'loading',
      productList: [],
      productsPerPage: { type: Number, default: 32 },
      pageNumber: 1,
      performanceMode: false,
      hasNextPage: false,
      promoIds: [],
      currentFilter: '',
    };
  },

  computed: {
    getFilteredProducts() {
      return filteredProducts(
        this.products,
        this.filters,
        this.state,
        this.performanceMode,
      );
    },

    queryVariables() {
      return {
        country: this.$variable('store.country'),
        handle: {
          handle: this.liquid.promoFilterHandle
            .toLowerCase()
            .replace(/ /g, '-'),
          type: 'product_variant_list',
        },
        language: this.$variable('store.language'),
      };
    },
  },

  watch: {
    /**
     * Watch partial to update product list.
     */
    partial() {
      this.products = this.partial
        ? this.allProducts.slice(0, 4)
        : this.allProducts;
    },
  },

  async mounted() {
    cnvs.EventBus.listen('campaign:countdown-finished', () => {
      this.partial = false;
    });

    if (!this.discounts?.length) {
      this.state = 'ready';
      return;
    }

    /** Special Promo - to add extra filter */
    if (this.liquid.promoFilterHandle && this.liquid.promoFilterText) {
      await this.fetchPromoFilter();
    }

    /**
     * Get products.
     * - Store all products separately so they can be used when partial updates.
     */

    this.productList = this.liquid.productList || [];

    this.performanceMode = this.liquid.useCacheList && this.productList.length;

    if (this.performanceMode) {
      await this.executePerformanceMode();
    } else {
      const products = await this.getProducts();
      const productsWithoutDuplicates = this.deDuplicateProducts(products);

      this.allProducts =
        this.liquid.productSorting.length > 0
          ? this.sortProducts(
              productsWithoutDuplicates,
              this.liquid.productSorting,
            )
          : productsWithoutDuplicates;

      if (this.liquid.combineVariants) {
        this.allProducts = this.getCombinedVariants(false, this.allProducts);
      }

      this.products = this.partial
        ? this.allProducts.slice(0, 4)
        : this.allProducts;

      this.filterOptions = buildFilterOptions(
        this.products,
        this.discountPreview,
      );

      this.addPromoFilter();
      this.filters = parseURLParams(this.filterOptions);
      this.pageNumber = getPageNumber();
    }

    this.state = 'ready';
  },

  methods: {
    async executePerformanceMode() {
      this.initialLoad = true;
      this.filterOptions = buildFilterOptions(
        this.productList,
        this.discountPreview,
        this.performanceMode,
      );

      this.addPromoFilter();

      this.filters = parseURLParams(this.filterOptions);
      this.pageNumber = getPageNumber();

      this.productsPerPage = this.$variable('settings.pagination.products');
      this.preparedCacheList = this.productList.map((product) => {
        return {
          productId: product.productId,
          variantId: product.variantId,
          isVariant: product.variantId,
          discount: product.discount,
        };
      });
      await this.getProductsByList();
    },

    addPromoFilter() {
      if (this.promoIds?.length && this.getPromoList().length > 0) {
        this.filterOptions.Discount.push(this.liquid.promoFilterText);
      }
    },

    /**
     * Fetch products/variants for promo filter.
     * - from the promo metaobject Product/Variant list.
     */
    async fetchPromoFilter() {
      const response = await cnvs.Query({
        query: fetchMetaobjectCard,
        variables: {
          ...this.queryVariables,
        },
      });

      if (!response?.metaobject) {
        return;
      }
      let transformedData = removeEdges(
        response.metaobject.fields.filter((field) => field.references !== null),
      ).map(({ id, product }) => {
        const isVariant = id.includes('ProductVariant');
        const thisId = id.split('/').pop();
        return {
          isVariant,
          productId: isVariant ? product.id.split('/').pop() : thisId,
          variantId: isVariant ? thisId : null,
        };
      });

      if (response?.metaobject.collection) {
        const products = [];
        transformEdges(
          response?.metaobject.collection?.reference.products,
        ).forEach((node) => {
          products.push(formatProduct(node));
        });
        // If the promo filter is a collection, we can't use variants
        transformedData = products.map((product) => {
          return {
            productId: product.id,
            variantId: null,
            isVariant: null,
          };
        });
      }
      this.promoIds = transformedData;
    },

    /**
     * Get products from the discount campaign.
     * - Iterate through each campaign discount object to calculate include and
     *   excludes independently.
     * @returns {Array<Object>}
     */
    getProducts() {
      return new Promise(async (resolve, reject) => {
        try {
          let products = [];

          /**
           * Go through each discount object in campaign.
           */
          for (const object of this.discounts) {
            // eslint-disable-next-line no-await-in-loop
            const include = await this.getIncludeProducts(object);
            // eslint-disable-next-line no-await-in-loop
            const exclude = await this.getExcludeIds(object);

            /**
             * Create products list for this discount.
             */

            products.push(formatCampaignProducts(include, exclude));
            products = products.flat();
          }

          if (!products.length) {
            this.state = 'ready';
          }

          resolve(products);
        } catch (error) {
          cnvs.ReportError('Failed get campaign products', error);
          reject(error);
        }
      });
    },

    getProductsByList(keepPrevious = false) {
      this.state = 'loading';
      if (!keepPrevious) {
        this.products = [];
      }
      this.initialLoad = !keepPrevious;

      return new Promise((resolve, reject) => {
        let discountForRequest = this.filters.Discount
          ? this.filters.Discount.join()
          : '';

        /** If there is a filter that does not exist, default to All */
        if (
          discountForRequest !== '' &&
          !this.filterOptions?.Discount.includes(discountForRequest)
        ) {
          this.removeFilters = { ...this.filters };
          discountForRequest = '';
          this.filters = {};
        }

        let filteredList = this.preparedCacheList.filter(
          (product) =>
            !this.filters.Discount ||
            this.filters.Discount.includes(product.discount.toString()),
        );

        const isPromoAndHasCurrentPromoFilter = () => {
          return (
            this.promoIds?.length &&
            (this.currentFilter.includes(this.liquid.promoFilterText) ||
              this.filters.Discount?.includes(this.liquid.promoFilterText))
          );
        };

        if (isPromoAndHasCurrentPromoFilter()) {
          filteredList = this.getPromoList();
        }

        const ids = filteredList
          .slice(
            keepPrevious ? this.productsPerPage * this.pageNumber : 0,
            keepPrevious
              ? this.productsPerPage * (this.pageNumber + 1)
              : this.productsPerPage * this.pageNumber,
          )
          .map((product) => {
            return product.isVariant
              ? transformId(product.variantId, 'ProductVariant')
              : transformId(product.productId, 'Product');
          });

        const getFormattedProducts = async () => {
          try {
            /**
             * Load nodes from included IDs.
             */
            const response = await this.fetchIdsInChunks(ids);

            const currentFilter = this.filters.Discount
              ? this.filters.Discount.join()
              : '';

            if (currentFilter !== discountForRequest) {
              resolve();
              return;
            }

            /**
             * Return unformatted nodes.
             */
            const products = formatCampaign(response)
              .map((node) => {
                if (node.variantNodeId) {
                  return formatProduct(node, {
                    variantId: decodeApiId(node.variantNodeId),
                  });
                }
                return formatProduct(node);
              })
              .flat();

            if (
              this.liquid.combineVariants &&
              !isPromoAndHasCurrentPromoFilter()
            ) {
              this.allProducts = this.getCombinedVariants(
                keepPrevious,
                products,
              );
            } else {
              this.allProducts = keepPrevious
                ? [...this.allProducts, ...products]
                : products;

              // remove unavailable variants for special promo filter
              if (isPromoAndHasCurrentPromoFilter()) {
                this.allProducts = this.allProducts.filter((product) => {
                  return (
                    !product.isVariant ||
                    product.selectedOrFirstAvailableVariant?.availableState ===
                      'available'
                  );
                });
              }
            }

            this.pageNumber = keepPrevious
              ? this.pageNumber + 1
              : this.pageNumber;

            updateURL(this.removeFilters, this.pageNumber, this.filters);
            this.removeFilters = {};

            this.products = this.partial
              ? this.allProducts.slice(0, 4)
              : this.allProducts;

            this.hasNextPage =
              filteredList.length > this.productsPerPage * this.pageNumber;

            resolve();
          } catch (error) {
            cnvs.ReportError('Failed to load included IDs', error);
            reject(error);
          } finally {
            this.state = 'ready';
            await this.$nextTick();
            this.initialLoad = false;
          }
        };

        getFormattedProducts();
      });
    },

    /**
     * Filter promo IDs based on the prepared cache list to ensure only available promos are included
     * @returns {Array} - Filtered promo IDs
     */
    getPromoList() {
      return this.promoIds.filter((promo) => {
        const flatIds = this.productList.flatMap(
          (product) => product.productId || product.variantId,
        );
        return (
          flatIds.includes(promo.productId.toString()) ||
          flatIds.includes(promo.variantId?.toString())
        );
      });
    },

    /**
     * Due to limitations of the GraphQL API, we need to fetch IDs in chunks of 32
     * as we do on infinite loading.
     * @param {Array} array - Array of IDs to fetch.
     */
    async fetchIdsInChunks(array) {
      let response = { nodes: [] };
      const chunkSize = 32;
      const promises = [];

      // Split the array into chunks of 32 and create promises for each query
      for (let index = 0; index < array.length; index += chunkSize) {
        const chunk = array.slice(index, index + chunkSize);

        const promise = new Promise((resolve, reject) => {
          cnvs
            .Query({
              query: nodesByIdProductCardQuery,
              variables: {
                country: this.$variable('store.country'),
                ids: chunk,
                language: this.$variable('store.language'),
                limit: this.partial ? 4 : 250,
              },
            })
            .then((productData) => {
              resolve(productData);
            })
            .catch((error) => {
              cnvs.ReportError('Failed to load included IDs', error);
              reject(error);
            });
        });

        promises.push(promise);
      }

      try {
        const results = await Promise.all(promises);
        const formattedResponse = results.reduce((acc, result) => {
          if (result.nodes && Array.isArray(result.nodes)) {
            return acc.concat(result.nodes);
          }
          return acc;
        }, []);

        response = {
          nodes: formattedResponse,
        };
        return response;
      } catch (error) {
        // Handle any errors from Promise.all here if necessary
        cnvs.ReportError('Error in fetching chunked IDs:', error);
        return response;
      }
    },

    getCombinedVariants(keepPrevious, products) {
      let combinedProducts = keepPrevious ? [...this.allProducts] : [];

      products
        .filter(
          (product) =>
            !(product.isVariant && product.availableState === 'hidden'),
        )
        .forEach((product) => {
          const existingProduct = combinedProducts.find((addedProduct) => {
            return (
              addedProduct.id === product.id &&
              getProductDiscount(addedProduct, true) ===
                getProductDiscount(product, true)
            );
          });

          if (existingProduct) {
            combinedProducts = combinedProducts.map((oldProduct) => {
              if (
                oldProduct.id === product.id &&
                getProductDiscount(oldProduct, true) ===
                  getProductDiscount(product, true)
              ) {
                const existingVariant = oldProduct.variants.find(
                  (variant) =>
                    variant.id === product.selectedOrFirstAvailableVariant.id,
                );

                const updatedVariants = existingVariant
                  ? oldProduct.variants
                  : [
                      ...oldProduct.variants,
                      product.selectedOrFirstAvailableVariant,
                    ];

                return {
                  ...oldProduct,
                  isVariant: false,
                  variants: product.isVariant
                    ? updatedVariants
                    : product.variants,
                };
              }

              return oldProduct;
            });
          } else if (product.isVariant) {
            combinedProducts.push({
              ...product,
              variants: [product.selectedOrFirstAvailableVariant],
            });
          } else {
            combinedProducts.push(product);
          }
        });

      return combinedProducts;
    },

    /**
     * Get include products.
     * @param {Object} object - Campaign discount object.
     * @returns {Promise<Array>} - Unformatted product responses.
     */
    getIncludeProducts(object) {
      return new Promise(async (resolve, reject) => {
        try {
          let includeCollectionIds = [];
          let includeProductIds = [];
          let includeVariantIds = [];

          if (object.include?.collectionIds?.length) {
            includeCollectionIds = object.include.collectionIds.map((id) => {
              return transformId(id, 'Collection');
            });
          }

          if (object.include?.productIds?.length) {
            includeProductIds = object.include.productIds.map((id) => {
              return transformId(id, 'Product');
            });
          }

          if (object.include?.variantIds?.length) {
            includeVariantIds = object.include.variantIds.map((id) => {
              return transformId(id, 'ProductVariant');
            });
          }

          const ids = [
            ...includeCollectionIds,
            ...includeProductIds,
            ...includeVariantIds,
          ];

          /**
           * Load nodes from included IDs.
           */
          const response = await this.fetchIdsInChunks(ids);

          resolve(formatCampaign(response));
        } catch (error) {
          cnvs.ReportError('Failed to load included IDs', error);
          reject(error);
        }
      });
    },

    /**
     * Get exclude IDs.
     * @param {Object} object - Campaign discount object.
     * @returns {Promise<Object>} - Separate product and variant IDs.
     */
    getExcludeIds(object) {
      return new Promise(async (resolve, reject) => {
        try {
          let excludeProductIds = [];
          let excludeVariantIds = [];

          if (object.exclude?.productIds?.length) {
            excludeProductIds = object.exclude.productIds.map((id) => {
              return transformId(id, 'Product');
            });
          }

          if (object.exclude?.variantIds?.length) {
            excludeVariantIds = object.exclude.variantIds.map((id) => {
              return transformId(id, 'ProductVariant');
            });
          }

          if (!object.exclude?.collectionIds?.length) {
            resolve({
              productIds: excludeProductIds,
              variantIds: excludeVariantIds,
            });

            return;
          }

          /**
           * Have to load excluded collection IDs to get excluded product IDs
           * from within the collection.
           */
          const excludeCollectionIds = object.exclude.collectionIds.map(
            (id) => {
              return transformId(id, 'Collection');
            },
          );

          const response = await cnvs.Query({
            query: excludedNodesByIdQuery,
            variables: {
              country: this.$variable('store.country'),
              ids: excludeCollectionIds,
              language: this.$variable('store.language'),
              limit: 250,
            },
          });

          const excludeCollectionProductIds =
            formatExcludedCollection(response);

          /**
           * Add excluded product IDs from excluded collections.
           */
          excludeProductIds = [
            ...excludeProductIds,
            ...excludeCollectionProductIds,
          ];

          resolve({
            productIds: excludeProductIds,
            variantIds: excludeVariantIds,
          });
        } catch (error) {
          cnvs.ReportError('Failed to load excluded IDs', error);
          reject(error);
        }
      });
    },

    /**
     * De-duplicate products.
     * @param {Array<Object>} products - All products.
     * @returns {Array<Object>}
     */
    deDuplicateProducts(products) {
      const uniqueIds = [];

      const uniqueProducts = products.filter((product) => {
        if (product.isVariant) {
          if (uniqueIds.includes(product.selectedOrFirstAvailableVariant.gid)) {
            return false;
          }

          uniqueIds.push(product.selectedOrFirstAvailableVariant.gid);
          return true;
        }

        if (uniqueIds.includes(product.gid)) {
          return false;
        }

        uniqueIds.push(product.gid);
        return true;
      });

      return uniqueProducts;
    },

    /**
     * Sort products based on Productvariant Ids.
     * @param {Array<Object>} products - All products.
     * @param {Array<Object>} ids - list with productvariant Ids.
     * @returns {Array<Object>}
     */
    sortProducts(products, ids) {
      const transformedIds = ids.map((id) => decodeApiId(id, 'ProductVariant'));
      const sortedProducts = [];

      transformedIds.forEach((id) => {
        const sortProductIndex = products.findIndex((product) => {
          const variantIds = product.variants.map((variant) => variant.id);
          return (
            (product.isVariant &&
              product.selectedOrFirstAvailableVariant.id === id) ||
            (!product.isVariant && variantIds.includes(id))
          );
        });

        /* if product for this sorting place is found add it to the sorted list,
          remove it from product list to avoid duplicates
        */
        if (sortProductIndex !== -1) {
          sortedProducts.push(products[sortProductIndex]);
          products.splice(sortProductIndex, 1);
        }
      });

      return sortedProducts.concat(products);
    },

    /**
     * Toggle filter.
     * @param {String} key - Filter key.
     * @param {String} value - Filter value.
     */
    async toggleBadgeFilter(key, value) {
      if (!this.filters[key]) {
        this.filters[key] = [];
      }

      this.currentFilter = value;
      this.filters[key] = [value];
      updateURL(this.removeFilters, this.pageNumber, this.filters);
      this.removeFilters = {};

      if (this.performanceMode) {
        this.pageNumber = 1;
        await this.getProductsByList();
      }
    },

    /**
     * Reset all filters.
     */
    async resetFilters() {
      this.removeFilters = { ...this.filters };
      this.filters = {};
      this.currentFilter = '';

      if (this.performanceMode) {
        this.pageNumber = 1;
        await this.getProductsByList();
      }

      updateURL(this.removeFilters, this.pageNumber, this.filters);
      this.removeFilters = {};
    },
  },
};
</script>

<style lang="scss">
@import './campaign-product-list';
</style>
